import { ref, onMounted, onUnmounted, nextTick } from 'vue';

/**
 * useFloatingMini — posição fixa arrastável para os mini cards flutuantes
 * (afinador, metrônomo), sobre o DraggableWrapper do sistema.
 *
 * - Default: canto inferior direito (com `bottomGap` extra opcional, ex. para
 *   empilhar acima de outra mini).
 * - Persiste em localStorage após o primeiro arraste; posição do usuário
 *   sempre vence o default.
 * - Sem posição salva, corrige com o tamanho medido no mount + resize.
 */
export function useFloatingMini(storageKey, opts = {}) {
  const margin = opts.margin ?? 16;
  const estimate = opts.estimate ?? { w: 220, h: 90 };
  const getBottomGap = opts.getBottomGap ?? (() => 0);

  const defaultPos = (w, h) => ({
    x: Math.max(0, window.innerWidth - w - margin),
    y: Math.max(0, window.innerHeight - h - margin - getBottomGap()),
  });

  const loadSaved = () => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (typeof parsed?.x === 'number' && typeof parsed?.y === 'number') {
        return { x: parsed.x, y: parsed.y };
      }
    } catch {
      // storage indisponível — segue com default
    }
    return null;
  };

  const saved = loadSaved();
  const hasCustomPos = ref(!!saved);
  const pos = ref(saved ?? defaultPos(estimate.w, estimate.h));
  const wrapperRef = ref(null);

  const measure = () => {
    const el = wrapperRef.value?.$el ?? wrapperRef.value;
    if (!el || typeof el.offsetWidth !== 'number') return null;
    return { w: el.offsetWidth, h: el.offsetHeight };
  };

  const recomputeDefault = () => {
    if (hasCustomPos.value) return;
    const size = measure() ?? estimate;
    pos.value = defaultPos(size.w, size.h);
  };

  const savePos = ({ x, y }) => {
    hasCustomPos.value = true;
    pos.value = { x, y };
    try {
      localStorage.setItem(storageKey, JSON.stringify({ x, y }));
    } catch {
      // ignora
    }
  };

  const handleResize = () => recomputeDefault();

  onMounted(() => {
    nextTick(() => recomputeDefault());
    window.addEventListener('resize', handleResize);
  });

  onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
  });

  return { pos, wrapperRef, savePos, recomputeDefault, hasCustomPos };
}
